import { Box, Badge, VStack, Image, Checkbox } from '@chakra-ui/react';
import { MatchDetails } from './MatchDetails';
import { MatchActions } from './MatchActions';
import { getDocumentUrl } from '../../utils/helpers';

interface MatchCardProps {
  match: any;
  submittedFaceUrl: string | null;
  API_URL: string;
  onStatsOpen: () => void;
  setSelectedMatchStats: (match: any) => void;
  isCompact?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: (match: any, selected: boolean) => void;
}

export const MatchCard = ({ 
  match, 
  submittedFaceUrl, 
  API_URL, 
  onStatsOpen, 
  setSelectedMatchStats,
  isCompact = false,
  isSelectable = false,
  isSelected = false,
  onSelect
}: MatchCardProps) => {
  const filename = match.image_path.split('/').pop();
  const imageUrl = `${API_URL}/api/faces/${filename}`;

  const getSourceBadgeProps = (source: string) => {
    const colorScheme = {
      'fugitive_database': 'red.700',
      'slack_mattermost_export': 'purple.700',
      'satg_suspects': 'orange.700',
      'misc_intel': 'teal.700',
      'oj_bookings': 'green.700',
      'rici_database': 'blue.700',
      'rockland_rici': 'cyan.700'
    }[source] || 'blue.700';

    const label = {
      'rici_database': 'Westchester Bookings',
      'fugitive_database': 'Westchester Fugitive Database',
      'slack_mattermost_export': 'Slack Archive',
      'satg_suspects': 'SATG Suspects',
      'misc_intel': 'Misc Intel Documents',
      'oj_bookings': 'Northern Bookings',
      'rockland_rici': 'Rockland Bookings'
    }[source] || source;

    return { colorScheme, label };
  };

  return (
    <Box 
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      bg="gray.700"
      p={4}
      display="flex"
      flexDirection="column"
      h="full"
      position="relative"
      border={isSelected ? "2px solid" : "1px solid"}
      borderColor={isSelected ? "blue.400" : "gray.600"}
      transition="all 0.2s ease-in-out"
      _hover={{
        borderColor: isSelectable ? "blue.400" : "gray.600",
        cursor: isSelectable ? "pointer" : "default"
      }}
      onClick={() => {
        if (isSelectable && onSelect) {
          onSelect(match, !isSelected);
        }
      }}
    >
      {isSelectable && (
        <Box
          position="absolute"
          top={2}
          right={2}
          zIndex={2}
          onClick={(e) => {
            e.stopPropagation();
            if (onSelect) {
              onSelect(match, !isSelected);
            }
          }}
        >
          <Checkbox
            isChecked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              if (onSelect) {
                onSelect(match, e.target.checked);
              }
            }}
            colorScheme="blue"
            size="lg"
            bg="gray.700"
            p={1}
            borderRadius="md"
          />
        </Box>
      )}
      <VStack spacing={1} align="stretch" flex="1">
        <Image 
          src={imageUrl} 
          alt={match.details?.name || 'Unknown person'} 
          borderRadius="md"
          objectFit="cover"
          w="100%"
          h="auto"
          aspectRatio={1}
        />
        <Badge
          bg={getSourceBadgeProps(match.source).colorScheme}
          color="white"
          variant="solid"
          fontSize="xs"
          p={1.5}
          borderRadius="sm"
          textTransform="uppercase"
          letterSpacing="0.5px"
          textAlign="center"
        >
          {getSourceBadgeProps(match.source).label}
        </Badge>

        <MatchDetails 
          match={match} 
          API_URL={API_URL} 
          isCompact={isCompact} 
        />
      </VStack>

      <Box mt="auto" pt={4}>
        <VStack spacing={2}>
          <Box 
            as="button"
            fontSize="sm" 
            color="white" 
            cursor="pointer" 
            onClick={() => {
              const fullImageUrl = getDocumentUrl(match.source, match.raw_image_path, API_URL);
              window.open(fullImageUrl, '_blank');
            }}
            p={1.5}
            borderRadius="md"
            bg="gray.600"
            _hover={{ 
              bg: "gray.500",
            }}
            transition="all 0.2s ease-in-out"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            w="full"
          >
            View Full {match.source === 'rici_database' || match.source === 'oj_bookings' ? 'Booking' : 'Document'}
            <span>→</span>
          </Box>

          <Box 
            as="button"
            fontSize="sm" 
            color="white" 
            cursor="pointer" 
            onClick={() => {
              setSelectedMatchStats(match);
              onStatsOpen();
            }}
            p={1.5}
            borderRadius="md"
            bg="gray.600"
            _hover={{ 
              bg: "gray.500",
            }}
            transition="all 0.2s ease-in-out"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            w="full"
          >
            View Match Statistics
            <span>→</span>
          </Box>

          <MatchActions 
            match={match} 
            submittedFaceUrl={submittedFaceUrl}
            API_URL={API_URL}
          />
        </VStack>
      </Box>
    </Box>
  );
}; 